import {FC, ReactElement, useEffect, useState, useMemo} from 'react';
import {ownerPath, videoPath, classPath} from 'routes';
import { Link } from 'react-router-dom';
import useAuthRedirect from 'hooks/useAuthRedirect';
import {Container, Grid, Typography, useMediaQuery, useTheme, Fab, Box, Skeleton, IconButton} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { useTranslation } from 'react-i18next';
import VideoSubjectSkeleton from 'components/VideoSubjectSkeleton';
import {usePlayback} from 'contexts/WatchContext';
import TitleHeader from 'components/TitleHeader';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VideoSubject from 'components/VideoSubject';
import {VideoSubjectMetadataMessage} from '@/types/VideoSubjectMetadata';
import {useActorLibraryQuery} from 'hooks/apiHooks';
import OverflowFilterBar from 'components/OverflowFilterBar';
import { NoSnippet } from 'utils/NoSnippet';

const ActorLibrary: FC = (): ReactElement => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { t } = useTranslation(['titleLibrary', 'titleToolbar']);
    const { play, isQueueMatch, currentVideo } = usePlayback();
  // Scroll to top on page mount
  useEffect(() => { window.scrollTo(0, 0); }, []);
  // Set document title to Library header
  useEffect(() => {
    document.title = `${t('title')} - ${t('library')}`;
  }, [t]);

  // Local filter state: class category and language
  const [selectedFilter, setSelectedFilter] = useState<{
    titleClass: string;
    titleLingo: string;
  } | null>(null);
  // Fetch library, filtered by class and lingo if selected
  const { data, error, isLoading } = useActorLibraryQuery(
    selectedFilter?.titleClass,
    selectedFilter?.titleLingo
  );
  // Redirect to login on auth error
  useAuthRedirect(error, { replace: true });
  // Class filter options and video list
  const classBlock = useMemo(
    () => data?.classMetadata?.classMetadata ?? [],
    [data?.classMetadata?.classMetadata]
  );
  const videoBlock: VideoSubjectMetadataMessage[] = data?.videoMetadata?.videoMetadata ?? [];
    // Order-sensitive match against shadow queue
    const queueMatch = isQueueMatch(videoBlock);
    // Disable FAB only when queue matches and a video is currently playing
    const disableFab = queueMatch && Boolean(currentVideo);
  if (isLoading) {
    // Skeleton layout while loading
    return (
      <Container maxWidth="xl" disableGutters={isSmallScreen}>
        {/* Header skeleton */}
        <Box sx={{ px: { sm: 2 }, mb: 2 }}>
          <Skeleton variant="rectangular" width="100%" height={theme.spacing(17)} />
        </Box>
        {/* Grid skeleton for videos */}
        <Grid container sx={{ mx: { xs: -1, sm: 0 } }}>
          {Array.from({ length: 12 }).map((_, idx) => (
            <Grid
              key={idx}
              size={{ xs: 6, md: 4, xl: 3 }}
              sx={{ px: { xs: 1, sm: 2 }, py: 1 }}
            >
              <VideoSubjectSkeleton key={idx} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }
  if (error) {
    return <Typography color="error">{t('errorLoading')}</Typography>;
  }
  // Empty library state
  if (!isLoading && !error && videoBlock.length === 0) {
    return (
      <Container maxWidth="xl" disableGutters={isSmallScreen}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          px: { xs: 1, sm: 2 },
          pt: 4,
          pb: 3,
        }}>
          <FavoriteBorderIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
          <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
            {t('emptyLibrary')}
          </Typography>
        </Box>
      </Container>
    );
  }

  // navigation now handled via Link props on VideoSubject

  return (
    <Container maxWidth="xl" disableGutters={isSmallScreen}>
      <Box sx={{ px: { sm: 2 }, mb: 2 }}>
        <TitleHeader
          title={t('library')}
          subtitle={t('browseSavedVideos')}
          waveform
        />
      </Box>
      {/* Class filter strip */}
      <NoSnippet>
      {classBlock.length > 0 && (
        <OverflowFilterBar
          filters={classBlock}
          selectedFilter={selectedFilter}
          onFilterSelect={setSelectedFilter}
          itemSx={{ cursor: 'filter' }}
          moreSx={{ cursor: 'filter' }}
        />
      )}
      </NoSnippet>
      <Grid container sx={{ mx: { xs: -1, sm: 0 }, pt: 0, pb: 0 }}>
        {videoBlock.map((video) => (
          <Grid
            key={video.musicVideo}
            size={{ xs: 6, md: 4, xl: 3 }}
            sx={{ px: { xs: 1, sm: 2 }, py: 1 }}
          >
            <VideoSubject
              video={video}
              videoLink={{
                to: videoPath(video.musicVideo),
                state: { facadeQueue: videoBlock, shadowQueue: videoBlock },
              }}
              ownerLink={{
                to: ownerPath(video.musicOwner)
              }}
            />
          </Grid>
        ))}
      </Grid>
      {/* Explore more of this class outside your library */}
      {selectedFilter?.titleLingo && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5, mb: 2 }}>
          <IconButton
            component={Link}
            to={classPath(
              selectedFilter.titleLingo,
              selectedFilter.titleClass
            )}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      )}
      {/* Queue or Shuffle FAB for Library page */}
      {videoBlock.length > 0 && (
        <Fab
          color="primary"
          disabled={disableFab}
          onClick={() => {
            play(videoBlock[0], videoBlock);
          }}
          sx={{
            position: 'fixed',
            bottom: isMobile ? theme.spacing(9) : theme.spacing(2),
            right: theme.spacing(2),
          }}
        >
          <PlayArrowIcon />
        </Fab>
      )}
    </Container>
  );
};

export default ActorLibrary;
